import { Box } from '@mui/material';
import { useRef } from 'react';
import { CropRect } from './types';

const HANDLE_SIZE = 10;
const MIN_CROP_SIZE = 20;

type DragMode = 'move' | 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'e' | 'w';

type CropToolProperties = {
  cropRect: CropRect | undefined;
  onCropChange: (rect: CropRect) => void;
  onCropEnd: (rect: CropRect) => void;
  containerWidth: number;
  containerHeight: number;
  naturalWidth: number;
  naturalHeight: number;
};

function computeMovedRect(start: CropRect, dx: number, dy: number, maxW: number, maxH: number): CropRect {
  return {
    x: Math.max(0, Math.min(maxW - start.width, start.x + dx)),
    y: Math.max(0, Math.min(maxH - start.height, start.y + dy)),
    width: start.width,
    height: start.height,
  };
}

function computeResizedRect({
  mode,
  start,
  dx,
  dy,
  bounds,
  minSize,
}: {
  mode: DragMode;
  start: CropRect;
  dx: number;
  dy: number;
  bounds: { width: number; height: number };
  minSize: { width: number; height: number };
}): CropRect {
  let { x, y, width, height } = start;

  if (mode.includes('w')) {
    const newX = Math.max(0, x + dx);
    width += x - newX;
    x = newX;
  }
  if (mode.includes('e')) {
    width = Math.min(bounds.width - x, start.width + dx);
  }
  if (mode.includes('n')) {
    const newY = Math.max(0, y + dy);
    height += y - newY;
    y = newY;
  }
  if (mode.includes('s')) {
    height = Math.min(bounds.height - y, start.height + dy);
  }

  return { x, y, width: Math.max(minSize.width, width), height: Math.max(minSize.height, height) };
}

const DimMask = ({ rect, width, height }: { rect: CropRect; width: number; height: number }) => (
  <>
    <defs>
      <mask id='cropMask'>
        <rect width={width} height={height} fill='white' />
        <rect x={rect.x} y={rect.y} width={rect.width} height={rect.height} fill='black' />
      </mask>
    </defs>
    <rect width={width} height={height} fill='rgba(0,0,0,0.5)' mask='url(#cropMask)' />
  </>
);

const MarchingAntsBorder = ({
  rect,
  onMoveStart,
}: {
  rect: CropRect;
  onMoveStart: (event: React.MouseEvent) => void;
}) => (
  <>
    <rect
      x={rect.x}
      y={rect.y}
      width={rect.width}
      height={rect.height}
      fill='transparent'
      stroke='#4a9e6e'
      strokeWidth={2}
      style={{ cursor: 'grab' }}
      onMouseDown={onMoveStart}
    />
    <rect
      x={rect.x}
      y={rect.y}
      width={rect.width}
      height={rect.height}
      fill='transparent'
      stroke='white'
      strokeWidth={2}
      strokeDasharray='8 8'
      style={{ cursor: 'grab', animation: 'marching-ants 0.5s linear infinite', pointerEvents: 'none' }}
    />
  </>
);

const RuleOfThirds = ({ rect }: { rect: CropRect }) => (
  <>
    {[1 / 3, 2 / 3].map(fraction => (
      <line
        key={`v-${fraction}`}
        x1={rect.x + rect.width * fraction}
        y1={rect.y}
        x2={rect.x + rect.width * fraction}
        y2={rect.y + rect.height}
        stroke='rgba(255,255,255,0.3)'
        strokeWidth={1}
      />
    ))}
    {[1 / 3, 2 / 3].map(fraction => (
      <line
        key={`h-${fraction}`}
        x1={rect.x}
        y1={rect.y + rect.height * fraction}
        x2={rect.x + rect.width}
        y2={rect.y + rect.height * fraction}
        stroke='rgba(255,255,255,0.3)'
        strokeWidth={1}
      />
    ))}
  </>
);

const ResizeHandles = ({
  rect,
  onDragStart,
}: {
  rect: CropRect;
  onDragStart: (mode: DragMode, event: React.MouseEvent) => void;
}) => {
  const { x, y, width: w, height: h } = rect;
  const handles: { mode: DragMode; x: number; y: number; cursor: string }[] = [
    { mode: 'nw', x, y, cursor: 'nw-resize' },
    { mode: 'ne', x: x + w, y, cursor: 'ne-resize' },
    { mode: 'sw', x, y: y + h, cursor: 'sw-resize' },
    { mode: 'se', x: x + w, y: y + h, cursor: 'se-resize' },
    { mode: 'n', x: x + w / 2, y, cursor: 'n-resize' },
    { mode: 's', x: x + w / 2, y: y + h, cursor: 's-resize' },
    { mode: 'w', x, y: y + h / 2, cursor: 'w-resize' },
    { mode: 'e', x: x + w, y: y + h / 2, cursor: 'e-resize' },
  ];

  return (
    <>
      {handles.map(handle => (
        <rect
          key={handle.mode}
          x={handle.x - HANDLE_SIZE / 2}
          y={handle.y - HANDLE_SIZE / 2}
          width={HANDLE_SIZE}
          height={HANDLE_SIZE}
          fill='#4a9e6e'
          stroke='white'
          strokeWidth={1}
          style={{ cursor: handle.cursor }}
          onMouseDown={event => onDragStart(handle.mode, event)}
        />
      ))}
    </>
  );
};

export const CropTool = ({
  cropRect,
  onCropChange,
  onCropEnd,
  containerWidth,
  containerHeight,
  naturalWidth,
  naturalHeight,
}: CropToolProperties) => {
  const svgReference = useRef<SVGSVGElement>(null);

  const scaleX = containerWidth / naturalWidth;
  const scaleY = containerHeight / naturalHeight;

  const toDisplay = (rect: CropRect): CropRect => ({
    x: rect.x * scaleX,
    y: rect.y * scaleY,
    width: rect.width * scaleX,
    height: rect.height * scaleY,
  });

  const toNatural = (rect: CropRect): CropRect => ({
    x: Math.round(rect.x / scaleX),
    y: Math.round(rect.y / scaleY),
    width: Math.round(rect.width / scaleX),
    height: Math.round(rect.height / scaleY),
  });

  const startDrag = (mode: DragMode, event: React.MouseEvent) => {
    if (!svgReference.current || !cropRect) return;
    event.stopPropagation();

    const svgRect = svgReference.current.getBoundingClientRect();
    const startX = event.clientX - svgRect.left;
    const startY = event.clientY - svgRect.top;
    const startDisplayRect = toDisplay(cropRect);

    let latestDisplayRect = startDisplayRect;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const dx = moveEvent.clientX - svgRect.left - startX;
      const dy = moveEvent.clientY - svgRect.top - startY;

      latestDisplayRect =
        mode === 'move'
          ? computeMovedRect(startDisplayRect, dx, dy, containerWidth, containerHeight)
          : computeResizedRect({
              mode,
              start: startDisplayRect,
              dx,
              dy,
              bounds: { width: containerWidth, height: containerHeight },
              minSize: { width: MIN_CROP_SIZE * scaleX, height: MIN_CROP_SIZE * scaleY },
            });

      onCropChange(toNatural(latestDisplayRect));
    };

    const handleMouseUp = () => {
      globalThis.removeEventListener('mousemove', handleMouseMove);
      globalThis.removeEventListener('mouseup', handleMouseUp);
      onCropEnd(toNatural(latestDisplayRect));
    };

    globalThis.addEventListener('mousemove', handleMouseMove);
    globalThis.addEventListener('mouseup', handleMouseUp);
  };

  const displayRect = cropRect ? toDisplay(cropRect) : undefined;
  if (!displayRect) return;

  return (
    <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
      <style>{`
        @keyframes marching-ants {
          to { stroke-dashoffset: -16; }
        }
      `}</style>
      <svg
        ref={svgReference}
        width={containerWidth}
        height={containerHeight}
        style={{ position: 'absolute', top: 0, left: 0 }}
      >
        <DimMask rect={displayRect} width={containerWidth} height={containerHeight} />
        <MarchingAntsBorder rect={displayRect} onMoveStart={event => startDrag('move', event)} />
        <RuleOfThirds rect={displayRect} />
        <ResizeHandles rect={displayRect} onDragStart={startDrag} />
      </svg>
    </Box>
  );
};
